import React, { FunctionComponent, useEffect, useState } from 'react';
import { Table, Spin, Empty } from 'antd';
import { useStore } from 'effector-react';
import claims, { getClaimsDetails } from '@stores/claims';
import { useParams } from 'react-router';
import css from './UserDetailClaims.module.less';
import IUser from '@interfaces/IUser';
import componentContent from './UserDetailClaims.content.json';
import user from '@stores/user';
import useTranslate from '@hooks/useTranslate';

interface UserDetailClaimsProps {}

const UserDetailClaims: FunctionComponent<UserDetailClaimsProps> = () => {
  const { item } = useStore(user);
  const { preloaders } = useStore(claims);
  const { userId } = useParams<{ userId: IUser['id'] }>();
  const [claimsData, setClaimsData] = useState<any[]>([]);

  const content = useTranslate(componentContent);

  useEffect(() => {
    if (userId && item?.nickname) {
      getClaimsDetails({ userId: userId, author: item?.nickname }).then((result) => {
        if (!result) return;

        const {
          accountClaimsCount,
          accountReasons,
          postsClaimsCount,
          postsReasons,
          commentsClaimsCount,
          commentsReasons,
          statusesClaimsCount,
          statusesReasons,
        } = result;

        const processedData = [
          {
            category: content.accountClaimsCount,
            count: accountClaimsCount || 0,
            reasons: accountReasons || {},
          },
          {
            category: content.postsClaimsCount,
            count: postsClaimsCount || 0,
            reasons: postsReasons || {},
          },
          {
            category: content.commentsClaimsCount,
            count: commentsClaimsCount || 0,
            reasons: commentsReasons || {},
          },
          {
            category: content.statusesClaimsCount,
            count: statusesClaimsCount || 0,
            reasons: statusesReasons || {},
          },
        ];

        setClaimsData(processedData);
      });
    }
  }, [userId, item]);

  const columns = [
    {
      title: content.category,
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: content.count,
      dataIndex: 'count',
      key: 'count',
    },
    {
      title: content.reason,
      dataIndex: 'reasons',
      key: 'reasons',
      render: (reasons: Record<string, number>) => (
        <ul style={{ paddingLeft: 16 }}>
          {Object.entries(reasons).map(([reason, count]) => (
            <li key={reason}>
              {reason}: {count}
            </li>
          ))}
        </ul>
      ),
    },
  ];

  const isListLoading = !!preloaders.getClaimsDetails;

  if (!claimsData.length && !isListLoading) {
    return <Empty />;
  }

  return (
    <div className={css.tableContainer}>
      <Spin spinning={isListLoading}>
        <Table
          dataSource={claimsData}
          columns={columns}
          rowKey={(record) => record.category}
          pagination={false}
        />
      </Spin>
    </div>
  );
};

export default UserDetailClaims;
